import { set, round } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { checkArray } from '../../Components/helper';
import { usePlanningActualState } from './Context/index';
import * as planningActualAction from './store/PlanningActual.action';
import useCalculationHooks from './Hooks/useCalculationHooks';

function TotalTable({ addEditAccess }) {
  const dispatch = useDispatch();
  const {
    setPlanningActual,
    planningActual: { overAllColumnValues, modifiedData, staticFields },
  } = usePlanningActualState();
  const { t } = useTranslation('common');

  const { setValuesBasesOnToggleMixToSales, calculateValuesWhenOverTotalSalesChanges } = useCalculationHooks();
  const [state, setState] = useState({
    overAllColumnValues: {},
  });

  useEffect(() => {
    if (overAllColumnValues) {
      setState((prevState) => ({
        ...prevState,
        overAllColumnValues: { ...overAllColumnValues },
      }));
    }
  }, [overAllColumnValues]);

  const toggleToMix = async (boolValue, keyName) => {
    const copyOverAllTotals = { ...state.overAllColumnValues };
    const copyState = { ...modifiedData };
    set(copyOverAllTotals, keyName, boolValue);
    if (keyName === 'isPlannedSales' && boolValue) {
      await setValuesBasesOnToggleMixToSales(copyState, 'plannedSales', copyOverAllTotals);
    }
    if (keyName === 'isActualSales' && boolValue) {
      await setValuesBasesOnToggleMixToSales(copyState, 'actualSales', copyOverAllTotals);
    } else {
      setPlanningActual((prevState) => ({
        ...prevState,
        overAllColumnValues: { ...copyOverAllTotals },
      }));
    }
    setPlanningActual((prevState) => ({
      ...prevState,
      validateColumns: {},
    }));
  };

  const onChangeofOverAllSales = async (value, keyName) => {
    const copyOverAllTotals = overAllColumnValues;
    set(copyOverAllTotals, keyName, value);
    await calculateValuesWhenOverTotalSalesChanges(modifiedData, copyOverAllTotals);
  };

  const returnZeroIfNan = (value) => {
    return Number.isNaN(value) ? 0 : value;
  };
  const checkNumberValue = (value) => {
    return value?.replace(/[^\d]/g, '');
  };
  const setInputOrReadOnly = (headerText) => {
    const { isPlannedSales, isActualSales } = state.overAllColumnValues;
    if (!isPlannedSales && headerText?.keyName === 'plannedSales') {
      return (
        <input
          className="form-control bordered-input"
          value={returnZeroIfNan(state?.overAllColumnValues?.[headerText?.keyName])}
          onChange={(e) => {
            const caret = e.target.selectionStart;
            const element = e.target;
            const inputValue = checkNumberValue(e.target.value);

            onChangeofOverAllSales(inputValue, 'plannedSales');
            window.requestAnimationFrame(() => {
              element.selectionStart = caret;
              element.selectionEnd = caret;
            });
          }}
          type="text"
          onKeyDown={(e) => {
            if (e.keyCode === 110) e.preventDefault();
          }}
          onBlur={() => handleTotalInput()}
        />
      );
    }
    if (isPlannedSales && headerText?.keyName === 'plannedSales') {
      return returnZeroIfNan(state?.overAllColumnValues?.[headerText?.keyName]);
    }
    if (!isActualSales && headerText?.keyName === 'actualSales') {
      return (
        <input
          className="form-control bordered-input"
          value={returnZeroIfNan(state?.overAllColumnValues?.[headerText?.keyName])}
          onChange={(e) => {
            const caret = e.target.selectionStart;
            const element = e.target;
            const inputValue = checkNumberValue(e.target.value);

            onChangeofOverAllSales(inputValue, 'actualSales');
            window.requestAnimationFrame(() => {
              element.selectionStart = caret;
              element.selectionEnd = caret;
            });
          }}
          type="text"
          onBlur={() => handleTotalInput()}
          onKeyDown={(e) => {
            if (e.keyCode === 110) e.preventDefault();
          }}
        />
      );
    }
    if (isActualSales && headerText?.keyName === 'actualSales') {
      return returnZeroIfNan(state?.overAllColumnValues?.[headerText?.keyName]);
    }
    if (['plannedMix', 'actualMix'].includes(headerText.keyName) && state.overAllColumnValues[headerText.keyName] > 100) {
      return round(returnZeroIfNan(state?.overAllColumnValues?.[headerText?.keyName]), 0);
    }
    return returnZeroIfNan(state?.overAllColumnValues?.[headerText?.keyName]);
  };

  const handleTotalInput = async () => {
    const copyState = modifiedData;
    modifiedData.overallTotals = state.overAllColumnValues;
    const res = await dispatch(planningActualAction.autoSaveData(copyState));

    if (res?.code) {
      setPlanningActual((prevState) => ({
        ...prevState,
        modifiedData: { ...modifiedData, _id: res?.data?._id },
      }));
    }
  };

  return (
    <div className="collapse-card">
      <Accordion defaultActiveKey="0">
        {checkArray(modifiedData?.category) && (
          <div className="d-flex">
            <table className="table custom-table layout-fixed planning-actual-tbl mt-0 total-table font-13 mr-3">
              <thead>
                <tr>
                  <th />
                  {staticFields.map((headerText, i) => {
                    return (
                      headerText.display && (
                        <th key={i} className="w-150px">
                          {headerText.name}
                        </th>
                      )
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>{t('PLANNING_ACTUAL.TOTAL_TABLE.TOTAL')}</td>
                  {staticFields.map((headerText, i) => {
                    return (
                      headerText.display && (
                        <td key={i} className="w-150px">
                          <div className="d-flex align-items-center justify-content-end">
                            {setInputOrReadOnly(headerText)}

                            {headerText.keyName === 'plannedSales' && addEditAccess && (
                              <button
                                className="btn-tbl-action"
                                title={t('PLANNING_ACTUAL.TOTAL_TABLE.EDIT')}
                                onClick={() => toggleToMix(!overAllColumnValues?.isPlannedSales, 'isPlannedSales')}
                                type="button"
                              >
                                <span className="icon-edit" />
                              </button>
                            )}
                            {headerText.keyName === 'actualSales' && addEditAccess && (
                              <button
                                className="btn-tbl-action"
                                title={t('PLANNING_ACTUAL.TOTAL_TABLE.EDIT')}
                                type="button"
                                onClick={() => toggleToMix(!overAllColumnValues?.isActualSales, 'isActualSales')}
                              >
                                <span className="icon-edit" />
                              </button>
                            )}
                          </div>
                        </td>
                      )
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Accordion>
    </div>
  );
}

export default TotalTable;
